
import { db } from "@/integrations/cloudflare/client";
import { fallbackFirstNamePrefixes, fallbackFirstNameSuffixes, fallbackLastNames, CACHE_REFRESH_TIME } from "./constants";

// Cache for name components
let cachedPrefixes: string[] | null = null;
let cachedSuffixes: string[] | null = null;
let cachedLastNames: string[] | null = null;
let lastCacheTime = 0;

// Function to load name components from the database
export const loadNameComponents = async (): Promise<void> => {
  const now = Date.now();
  // Only update cache every 5 minutes
  if (cachedPrefixes && cachedSuffixes && cachedLastNames && now - lastCacheTime < CACHE_REFRESH_TIME) {
    return;
  }

  try {
    // Load prefixes
    const { data: prefixData, error: prefixError } = await db
      .from('elf_name_prefixes')
      .select('prefix')
      .order('prefix');
    
    if (prefixError) throw prefixError;
    
    // Load suffixes
    const { data: suffixData, error: suffixError } = await db
      .from('elf_name_suffixes')
      .select('suffix')
      .order('suffix');
    
    if (suffixError) throw suffixError;
    
    // Load last names
    const { data: lastNameData, error: lastNameError } = await db
      .from('elf_last_names')
      .select('last_name')
      .order('last_name');
    
    if (lastNameError) throw lastNameError;

    if (prefixData && prefixData.length > 0) {
      cachedPrefixes = prefixData.map(item => item.prefix);
    } else {
      cachedPrefixes = fallbackFirstNamePrefixes;
    }

    if (suffixData && suffixData.length > 0) {
      cachedSuffixes = suffixData.map(item => item.suffix);
    } else {
      cachedSuffixes = fallbackFirstNameSuffixes;
    }

    if (lastNameData && lastNameData.length > 0) {
      cachedLastNames = lastNameData.map(item => item.last_name);
    } else {
      cachedLastNames = fallbackLastNames;
    }

    lastCacheTime = now;
  } catch (error) {
    console.error("Error loading name components:", error);
    // Fall back to hardcoded values if database fails
    cachedPrefixes = fallbackFirstNamePrefixes;
    cachedSuffixes = fallbackFirstNameSuffixes;
    cachedLastNames = fallbackLastNames;
  }
};

// Getters for cached values
export const getCachedPrefixes = (): string[] => {
  if (!cachedPrefixes) {
    return fallbackFirstNamePrefixes;
  }
  return cachedPrefixes;
};

export const getCachedSuffixes = (): string[] => {
  if (!cachedSuffixes) {
    return fallbackFirstNameSuffixes;
  }
  return cachedSuffixes;
};

export const getCachedLastNames = (): string[] => {
  if (!cachedLastNames) {
    return fallbackLastNames;
  }
  return cachedLastNames;
};

// Function to invalidate the cache
export const invalidateCache = (): void => {
  cachedPrefixes = null;
  cachedSuffixes = null;
  cachedLastNames = null;
  lastCacheTime = 0;
};
